/**
 * A single, shared, coarse clock for "is this happening right now?" UI.
 *
 * Anything derived from the current instant — the event status badge, the
 * calendar's live-event markers — goes stale the moment it renders unless
 * something re-evaluates it. Operators leave the events list and the calendar
 * open for a whole evening, which is exactly the window in which an event
 * starts and ends.
 *
 * One module-level interval drives every subscriber (not one timer per row),
 * and the snapshot is bucketed to the tick so React re-renders only on a real
 * boundary rather than on every animation frame.
 */

import { useSyncExternalStore } from "react";

/** How often liveness-derived UI re-evaluates. */
export const NOW_TICK_MS = 30_000;

const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  if (timer === null) {
    timer = setInterval(() => {
      listeners.forEach((l) => l());
    }, NOW_TICK_MS);
  }
  return () => {
    listeners.delete(onChange);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** Bucketed so the store value only changes once per tick. */
function snapshot(): number {
  return Math.floor(Date.now() / NOW_TICK_MS);
}

/**
 * The current instant, in epoch-ms, rounded DOWN to the tick boundary.
 * Stable within a tick (so it is a safe useMemo dependency) and changes at most
 * once every NOW_TICK_MS.
 */
export function useNowTick(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot) * NOW_TICK_MS;
}
